import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import {
  compareValues,
  SortDirection,
  toggleSortDirection,
} from '../../../lib/utils/sort';
import {
  ExamEventService,
  NotificationService,
  StatisticsSearchFilters,
} from '../../../services';
import { ExamEventStatistics } from '../../../types/ExamEventStatistics';

export interface SelectedEventInfos {
  selectedEventIds: number[];
  events: ExamEventStatistics[];
}

type SortableColumn =
  | 'testEventId'
  | 'testareaName'
  | 'testCenterName'
  | 'testEventTime'
  | 'eventdate'
  | 'eventtime'
  | 'maximumCapacity'
  | 'numberOfBookings'
  | 'utilizationRatio';

@Component({
  selector: 'app-exam-event-statistics-table',
  templateUrl:
    './exam-event-statistics-table.component.html',
  styleUrls: [
    './exam-event-statistics-table.component.scss',
  ],
})
export class ExamEventStatisticsTableComponent
  implements OnInit
{
  @Input() currentTestEventId?: number;
  @Input() searchFilters!: StatisticsSearchFilters;
  @Input() allowMultiSelect = true;

  @Output() activeEventSet = new EventEmitter<number>();
  @Output() eventChosen = new EventEmitter<number>();
  @Output() eventsSelectedForMultiAction =
    new EventEmitter<SelectedEventInfos>();

  eventStatistics: ExamEventStatistics[] = [];
  selectedEvents: ExamEventStatistics[] = [];

  sortColumn: SortableColumn = 'testEventTime';
  sortDirection: SortDirection = 'asc';

  activeRowId!: number;

  constructor(
    private examEventService: ExamEventService,
    private translateService: TranslateService,
    private notification: NotificationService
  ) {}

  ngOnInit(): void {
    this.loadAvailableEvents();
    if (this.currentTestEventId) {
      this.activeRowId = this.currentTestEventId;
    }
  }

  async loadAvailableEvents(): Promise<void> {
    const statistics =
      await this.examEventService.loadStatisticsList(
        this.searchFilters
      );
    this.eventStatistics = statistics.items;
    this.selectedEvents = [];

    if (this.currentTestEventId) {
      await this.setActiveEvent();
    }

    this.emitSelectionChange();
  }

  async setActiveEvent(): Promise<void> {
    const active = this.eventStatistics.find(
      (ev) => ev.testEventId === this.currentTestEventId
    );
    if (active) {
      this.activeRowId = active.testEventId;
      this.activeEventSet.emit(this.activeRowId);
    } else {
      await this.notification.error('error.general');
    }
  }

  newEventChosen(event: ExamEventStatistics): void {
    this.activeRowId = event.testEventId;
    this.eventChosen.emit(event.testEventId);
  }

  sortBy(column: SortableColumn) {
    const next = toggleSortDirection(
      this.sortColumn,
      column,
      this.sortDirection
    );
    this.sortColumn = next.column;
    this.sortDirection = next.direction;
  }

  get sortedEvents(): ExamEventStatistics[] {
    return [...this.eventStatistics].sort((a, b) => {
      const valA = this.getSortValue(a, this.sortColumn);
      const valB = this.getSortValue(b, this.sortColumn);
      return compareValues(valA, valB, this.sortDirection);
    });
  }

  getSortValue(
    data: ExamEventStatistics,
    column: SortableColumn
  ): any {
    switch (column) {
      case 'testEventId':
        return data.testEventId;
      case 'testareaName':
        return data.testareaName;
      case 'testCenterName':
        return data.testCenterName;
      case 'eventdate':
        return data.testEventTime;
      case 'eventtime':
        return DateTime.fromISO(
          data.testEventTime
        ).toFormat('HH:mm');
      case 'testEventTime':
        return data.testEventTime;
      case 'maximumCapacity':
        return data.maximumCapacity;
      case 'numberOfBookings':
        return data.numberOfBookings;
      case 'utilizationRatio':
        return data.utilizationRatio;
      default:
        return '';
    }
  }

  toggleRowSelection(event: ExamEventStatistics): void {
    const idx = this.selectedEvents.findIndex(
      (e) => e.testEventId === event.testEventId
    );
    if (idx > -1) {
      this.selectedEvents.splice(idx, 1);
    } else {
      this.selectedEvents.push(event);
    }
    this.emitSelectionChange();
  }

  areAllTestEventsSelected(): boolean {
    return (
      this.selectedEvents.length ===
      this.eventStatistics.length
    );
  }

  toggleAllTestEventsSelected(): void {
    this.areAllTestEventsSelected()
      ? (this.selectedEvents = [])
      : (this.selectedEvents = [...this.eventStatistics]);
    this.emitSelectionChange();
  }

  emitSelectionChange(): void {
    const selectedEventIds = this.selectedEvents.map(
      (ev) => ev.testEventId
    );
    this.eventsSelectedForMultiAction.emit({
      selectedEventIds,
      events: this.eventStatistics,
    });
  }

  isSelected(event: ExamEventStatistics): boolean {
    return this.selectedEvents.some(
      (e) => e.testEventId === event.testEventId
    );
  }

  get currentLanguage(): string {
    return this.translateService.currentLang;
  }
}
